import { api } from '@/api/client';

/** One system + its summed m² on a werf card. */
export interface WerfSystem {
  systeem: string;
  m2: number;
}

/** A single werf (fase) card as returned by the magazijnier day-werven endpoint.
 *  Shape mirrors the contract in plans/magazijnier-werven-view.md (Fase 1). */
export interface WerfCard {
  faseTaskId: number;
  planningTaskId: number;
  projectId: number;
  projectName: string;
  /** Fase / planning-task name — distinguishes multiple fases of the same project. */
  faseName: string | null;
  saleOrderName: string | null;
  werfAddress: string | null;
  /** Planned date (YYYY-MM-DD) of this werf — essential in search results across days. */
  date: string | null;
  installer: { name: string } | null;
  projectleider: { name: string } | null;
  /** Position of this day within the fase's planned days (1-based). Null for single-day werven. */
  dag: { index: number; total: number } | null;
  /** All planning tasks (scheduled days) of this fase, sorted chronologically.
   *  In search mode the card lists these under the fase header. */
  days: { date: string; planningTaskId: number }[];
  systems: WerfSystem[];
  collectorCount: number;
  totalM2: number;
  /** 'fase' = aggregate built from collectoren linked to this fase via materiaallijst;
   *  'project' = pre-backfill fallback summing all project collectoren. */
  aggregateScope: 'fase' | 'project';
}

export interface WervenResponse {
  /** Echoed query date (YYYY-MM-DD). Absent on search responses. */
  date?: string;
  werven: WerfCard[];
}

/**
 * Day-werven for the magazijnier. `date` must be a timezone-safe local date string
 * (YYYY-MM-DD) — never derive it from `toISOString()` (shifts a day back in CET/CEST).
 */
export async function fetchWervenByDate(date: string): Promise<WervenResponse> {
  return api<WervenResponse>(`/api/warehouse/werven?date=${encodeURIComponent(date)}`);
}

/** Search werven across ALL days by werf name, customer or S-number. Flat list, same card shape. */
export async function searchWerven(query: string): Promise<WervenResponse> {
  return api<WervenResponse>(`/api/warehouse/werven?q=${encodeURIComponent(query)}`);
}
